import { doneText, dueText, timeText } from 'helper/taskDates';
import { addDays, isoDay } from 'helper/timeLabels';

// The groups of the « À faire » tab, by due day.
export const TASK_GROUPS = ['late', 'today', 'tomorrow', 'later'];
// The columns of the kanban: the same, then the last tasks done.
export const KANBAN_COLUMNS = [...TASK_GROUPS, 'done'];
export const KANBAN_DONE_SHOWN = 30;
// The due filters, and the address parameters that open the page on one
// (« Voir les autres tâches en retard » of the home page: ?echeance=retard).
export const DUE_FILTERS = ['late', 'today', 'tomorrow', 'week', 'next', 'later'];
export const DUE_PARAMS = { retard: 'late', aujourdhui: 'today', demain: 'tomorrow', semaine: 'week' };

/**
 * The rows of the page, from the data of `crmApi.getTasks`: the open tasks
 * to do, and in « Terminées » the done tasks with the appointments and
 * callbacks ticked on the home page, the most recent first. An appointment
 * reads as a meeting, a callback as a call; neither is assigned.
 */
export const taskRows = (data) => {
  const fromTask = (task) => ({ ...task, kind: 'task', key: task.id });
  const fromAgenda = (item) => ({
    ...item,
    kind: item.kind,
    key: `${item.kind}-${item.id}`,
    type: item.kind === 'appointment' ? 'meeting' : 'call',
    dueTime: null,
    assigneeId: null,
    unreadComments: false,
  });
  return {
    todo: data.tasks.filter((task) => !task.doneAt).map(fromTask),
    done: [
      ...data.tasks.filter((task) => task.doneAt).map(fromTask),
      ...data.doneAgenda.map(fromAgenda),
    ].sort((a, b) => new Date(b.doneAt) - new Date(a.doneAt)),
  };
};

export const dueGroupOf = (row, now) => {
  const today = isoDay(now);
  if (row.dueOn < today) return 'late';
  if (row.dueOn === today) return 'today';
  if (row.dueOn === isoDay(addDays(now, 1))) return 'tomorrow';
  return 'later';
};

// Whether a row is due in the period of a filter; `day` for one given day.
export const dueMatches = (row, due, day, now) => {
  if (!due || due === 'all') return true;
  const today = isoDay(now);
  const monday = addDays(now, -((now.getDay() + 6) % 7));
  const [weekStart, weekEnd, nextStart, nextEnd] = [0, 6, 7, 13].map((days) => isoDay(addDays(monday, days)));
  switch (due) {
    case 'late':
      return row.dueOn < today;
    case 'today':
      return row.dueOn === today;
    case 'tomorrow':
      return row.dueOn === isoDay(addDays(now, 1));
    case 'week':
      return row.dueOn >= weekStart && row.dueOn <= weekEnd;
    case 'next':
      return row.dueOn >= nextStart && row.dueOn <= nextEnd;
    case 'later':
      return row.dueOn > nextEnd;
    case 'day':
      return !day || row.dueOn === day;
    default:
      return true;
  }
};

// What the search and the filters keep: the words in the title, the company
// or the contact, the chosen kinds of row (task, appointment, callback), the
// due period.
export const keepRow = (row, { query = '', types = [], due = 'all', day = null, now }) => {
  const words = query.trim().toLowerCase();
  const haystack = `${row.title} ${row.company?.name ?? ''} ${row.group?.name ?? ''} ${row.contactName ?? ''}`.toLowerCase();
  return (
    (!words || haystack.includes(words)) &&
    (!types.length || types.includes(row.kind)) &&
    dueMatches(row, due, day, now)
  );
};

// The groups of the list: by due day in « À faire », one in « Terminées ».
export const groupRows = (rows, tab, now) =>
  (tab === 'todo'
    ? TASK_GROUPS.map((id) => ({ id, rows: rows.filter((row) => dueGroupOf(row, now) === id) }))
    : [{ id: 'done', rows }]
  ).filter((group) => group.rows.length);

// The kanban: the tasks to do by due day, then the last tasks done (tasks
// only: an appointment or a callback does not move between columns).
export const boardColumns = (rows, keep, now) => {
  const columns = { late: [], today: [], tomorrow: [], later: [] };
  rows.todo.filter(keep).forEach((row) => columns[dueGroupOf(row, now)].push(row));
  columns.done = rows.done.filter((row) => row.kind === 'task' && keep(row)).slice(0, KANBAN_DONE_SHOWN);
  return columns;
};

// The day a card dropped in a column gets: today, tomorrow, or in a week.
export const dueOnForColumn = (column, now) =>
  isoDay(addDays(now, column === 'tomorrow' ? 1 : column === 'later' ? 7 : 0));

/**
 * A row ready to draw: its due group, whether it is late, its due day (or the
 * day it was done) and its time, in the page's texts (`text`: tasks.*).
 * `dueGroup`, not `group`: the row's own `group` is the company group it belongs to.
 */
export const describeRow = (row, { now, text }) => {
  const dueGroup = row.doneAt ? 'done' : dueGroupOf(row, now);
  return {
    ...row,
    dueGroup,
    late: dueGroup === 'late',
    dueLabel: row.doneAt ? doneText(row, text) : dueText(row, now, text),
    timeLabel: timeText(row),
  };
};
